import { useContext, FC } from 'react';
import { LastActivityCellProps } from './LastActivityCell.types';
import {
  Flex,
  Icon,
  Pill,
  Separator,
  Text,
  Timestamp,
  Tooltip,
} from '@/_components';
import { getDateAgo } from '@/_utils/dates';
import { UserContext } from '@/_contexts/UserProvider';

export const LastActivityCell: FC<LastActivityCellProps> = ({
  questionnaire,
}) => {
  const {
    sent_at,
    last_activity,
    created_at,
    last_activity_at,
    last_followed_up,
    reminders_disabled,
  } = questionnaire;

  const { user } = useContext(UserContext);

  const lastActivityShouldIncludeAllActivity: boolean =
    !!user?.overview_setting?.last_activity_should_include_all_activity;

  const isDraft: boolean = !!questionnaire?.is_draft;

  const tooltipContent: React.ReactNode = (
    <Flex isColumn={true} gap={4}>
      {lastActivityShouldIncludeAllActivity && (
        <>
          <Flex isColumn={true}>
            <Text type="tiny" color="currentcolor">
              Last user sign in:
            </Text>
            <Text type="tiny" color="currentcolor">
              <Timestamp timestamp={last_activity_at} isFromNow={true} />
            </Text>
          </Flex>
          <Separator isInverted={true} />
        </>
      )}

      {/* Created */}
      <Flex isColumn={true}>
        <Text type="tiny" color="currentcolor">
          {isDraft ? 'Saved on:' : 'Sent on:'}
        </Text>
        <Text type="tiny" color="currentcolor">
          <Timestamp timestamp={created_at} />
        </Text>
      </Flex>

      <Separator isInverted={true} />

      {lastActivityShouldIncludeAllActivity ? (
        <Flex isColumn={true}>
          {/* Last Reminder */}
          <Text type="tiny" color="currentcolor">
            Last reminder:
          </Text>
          <Text type="tiny" color="currentcolor">
            {last_followed_up ? (
              <Timestamp timestamp={last_followed_up} />
            ) : (
              'No Reminder Sent'
            )}
          </Text>
          {reminders_disabled && (
            <Text type="tiny" color="currentcolor">
              Reminder disabled
            </Text>
          )}
        </Flex>
      ) : (
        <Flex isColumn={true}>
          {/* Last Client Activity */}
          <Text type="tiny" color="currentcolor">
            Last client activity:
          </Text>
          {last_activity ? (
            <Text type="tiny" color="currentcolor">
              <Timestamp timestamp={last_activity} isFromNow={true} />
            </Text>
          ) : (
            <Text type="tiny" color="currentcolor">
              No client activity yet
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );

  const isNew: boolean = getDateAgo(created_at).includes('few seconds ago');

  const lastActivityToShow = last_activity || sent_at || created_at;

  return (
    <Tooltip>
      <Tooltip.Content>{tooltipContent}</Tooltip.Content>
      <Tooltip.Trigger>
        <Flex isYCentered={true} gap={2} isWrapping={true}>
          {isNew && (
            <Pill
              label={isDraft ? 'Saved' : 'Sent'}
              isDark={true}
              intent={isDraft ? 'accent' : 'success'}
              size="small"
            />
          )}
          <Text type="tiny">
            {lastActivityToShow ? (
              <Timestamp
                timestamp={lastActivityToShow}
                hideTime={true}
                isFromNow={true}
              />
            ) : (
              <Icon size={16} name="prohibit" color="disabled" />
            )}
          </Text>
        </Flex>
      </Tooltip.Trigger>
    </Tooltip>
  );
};
